import { useState, type FormEvent } from "react";
import { brandInfo } from "@/mocks/home";

const CATEGORY_OPTIONS = [
  "消费维权",
  "行业乱象",
  "产品质量",
  "公共服务",
  "职场内部",
  "其他线索",
];

const MAX_DETAIL = 500;

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function SubmitForm() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");
  const [detailCount, setDetailCount] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    // 反垃圾蜜罐校验：命中则不发送任何数据，返回与正常提交一致的反馈
    const guardValue = String(formData.get("website_alt") ?? "").trim();
    if (guardValue) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      setDetailCount(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch("https://readdy.ai/api/form/dah8jhjp14a1h3mr6f90", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = serverMessage.toLowerCase().includes("spam");
      const codeOk = parsed?.code === "OK";

      if (response.ok && codeOk && !isSpam) {
        setStatus("success");
        setFormError("");
        formEl.reset();
        setDetailCount(0);
      } else {
        setStatus("error");
        setFormError(
          serverMessage || "提交未成功，请稍后重试，或直接发送邮件联系我们。"
        );
      }
    } catch (error) {
      setStatus("error");
      setFormError(
        error instanceof Error && error.message
          ? `网络异常：${error.message}`
          : "网络异常，请检查连接后重试。"
      );
    }
  };

  return (
    <section id="submit" className="w-full bg-foreground-950 py-16 md:py-24 scroll-mt-20">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs md:text-sm font-medium tracking-widest uppercase text-accent-300">
              <span className="w-6 h-px bg-current inline-block"></span>
              我要投稿
            </span>
            <h2 className="mt-3 font-heading font-bold text-2xl md:text-4xl leading-snug text-background-50">
              把线索交给认真对待它的人
            </h2>
            <p className="mt-5 text-sm md:text-base leading-relaxed text-background-100/80">
              填写下面的表单即可提交爆料。内容只在核验环节被查看，发布时默认匿名；需要重点保护的信息，请在描述里单独说明。
            </p>

            <ul className="mt-8 space-y-4">
              {[
                { icon: "ri-time-line", text: "工作时段提交，初审平均 4 小时内反馈" },
                { icon: "ri-shield-check-line", text: "默认匿名发布，联系方式分层存放" },
                { icon: "ri-wallet-3-line", text: "采纳后 3 个工作日内结算稿费" },
                { icon: "ri-lock-2-line", text: "30 天内可要求删除投稿内容" },
              ].map((item) => (
                <li key={item.text} className="flex items-start gap-3 text-sm text-background-100/85">
                  <span className="w-9 h-9 shrink-0 rounded-md flex items-center justify-center bg-background-50/10 text-primary-300">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <span className="pt-2">{item.text}</span>
                </li>
              ))}
            </ul>

            <p className="mt-8 text-sm text-background-100/60">
              也可以发邮件至{" "}
              <a
                href={`mailto:${brandInfo.email}`}
                className="text-accent-300 hover:text-accent-200 underline underline-offset-4"
              >
                {brandInfo.email}
              </a>
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200">
              {status === "success" ? (
                <div className="py-10 text-center">
                  <span className="w-16 h-16 mx-auto rounded-full flex items-center justify-center bg-secondary-100 text-secondary-600">
                    <i className="ri-checkbox-circle-fill text-4xl"></i>
                  </span>
                  <h3 className="mt-5 font-heading font-bold text-xl text-foreground-950">
                    投稿已提交，感谢你的信任
                  </h3>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed max-w-md mx-auto">
                    我们会在工作时段内开始初审，通常 4 小时内给出反馈。若需要补充材料，编辑会通过你预留的联系方式联系你。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
                  >
                    <i className="ri-add-line"></i>
                    再提交一条线索
                  </button>
                </div>
              ) : (
                <form
                  id="baoliao-submit-form"
                  data-readdy-form
                  action="https://readdy.ai/api/form/dah8jhjp14a1h3mr6f90"
                  method="post"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="event_title" className="text-sm font-medium text-foreground-800">
                        事件概括 <span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="event_title"
                        name="event_title"
                        type="text"
                        required
                        maxLength={80}
                        placeholder="一句话说清发生了什么"
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="category" className="text-sm font-medium text-foreground-800">
                        线索类别 <span className="text-accent-600">*</span>
                      </label>
                      <select
                        id="category"
                        name="category"
                        required
                        defaultValue=""
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      >
                        <option value="" disabled>
                          请选择最接近的类别
                        </option>
                        {CATEGORY_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="region" className="text-sm font-medium text-foreground-800">
                        发生地区
                      </label>
                      <input
                        id="region"
                        name="region"
                        type="text"
                        maxLength={40}
                        placeholder="如：浙江杭州（可选）"
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="email" className="text-sm font-medium text-foreground-800">
                        联系方式（邮箱）
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        maxLength={120}
                        autoComplete="email"
                        placeholder="用于核验与稿费结算，可留空"
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-4">
                      <label htmlFor="detail" className="text-sm font-medium text-foreground-800">
                        事件经过 <span className="text-accent-600">*</span>
                      </label>
                      <span className="text-xs text-foreground-500">
                        {detailCount}/{MAX_DETAIL}
                      </span>
                    </div>
                    <textarea
                      id="detail"
                      name="detail"
                      required
                      rows={6}
                      maxLength={MAX_DETAIL}
                      onChange={(e) => setDetailCount(e.target.value.length)}
                      placeholder="按时间顺序写清起因、经过与当前进展，并说明信息来源与可核验的材料（如截图、票据、录音）。请不要超过 500 字。"
                      className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 leading-relaxed resize-none focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    ></textarea>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="anonymous"
                        value="是"
                        className="w-4 h-4 rounded border-background-400 text-primary-600 focus:ring-primary-300 cursor-pointer"
                      />
                      <span className="text-sm text-foreground-700">
                        需要匿名保护，请勿公开我的身份信息
                      </span>
                    </label>
                    <span className="text-xs text-foreground-500">
                      {brandInfo.replyNote}
                    </span>
                  </div>

                  {/* 反垃圾校验字段 */}
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="wei-field-guard"
                  />

                  {status === "error" && (
                    <p role="alert" className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-4 py-3 flex items-start gap-2">
                      <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-3.5 rounded-md bg-primary-500 text-background-50 font-medium text-sm md:text-base whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                        正在提交…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill text-lg"></i>
                        提交爆料
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}